import React from 'react';
import { MoreHorizontal } from 'lucide-react';
import { COLUMN_TYPES } from '../column-types';
import { Table } from '@tanstack/react-table';
import { useStatusBarInfo } from '../../../../hooks/useStatusBarInfo';
import { createSelectOption } from '../types/select-options';

interface Column {
  id: string;
  name: string;
  type: string;
  order_index: number;
  is_visible: boolean;
}

interface TableDefinition {
  id: string;
  name: string;
  topic_id: string;
}

interface TableColumnHeadersProps {
  table: Table<Record<string, unknown>>;
  editingColumnId: string | null;
  setEditingColumnId: React.Dispatch<React.SetStateAction<string | null>>;
  showColumnTypeMenu: string | null;
  setShowColumnTypeMenu: React.Dispatch<React.SetStateAction<string | null>>;
  columns: Column[];
  updateColumn: (tableId: string, columnId: string, data: Partial<Column>) => Promise<void>;
  deleteColumn: (tableId: string, columnId: string) => Promise<void>;
  tableDefinition: TableDefinition;
  isMobile: boolean;
  needsDesktopScrolling?: boolean;
  ColumnTypeMenu: React.ComponentType<{
    columnId: string;
    currentType: string;
    onSelect: (type: string) => void;
    onClose: () => void;
    columnIndex?: number;
    columnsCount?: number;
    onAddColumnAfter?: (columnId: string) => void;
    targetRef?: React.RefObject<HTMLElement>;
  }>;
  onAddColumnAfter?: (columnId: string) => void;
}

export function TableColumnHeaders({
  table,
  editingColumnId,
  setEditingColumnId,
  showColumnTypeMenu,
  setShowColumnTypeMenu,
  columns,
  updateColumn,
  tableDefinition,
  isMobile,
  needsDesktopScrolling = false,
  ColumnTypeMenu,
  onAddColumnAfter,
}: TableColumnHeadersProps) {
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // Create refs for column menu buttons
  const columnMenuButtonRefs = React.useRef<{ [key: string]: React.RefObject<HTMLButtonElement> }>(
    {}
  );
  return (
    <thead
      className={isMobile ? '' : 'sticky z-10 bg-[var(--content-bg-color)]'}
      style={{
        top: isMobile && isNativeApp ? `${statusBarHeight}px` : '0',
      }}
    >
      <tr>
        {/* Header for line numbers/row actions column */}
        <th
          className={`w-10 min-w-10 p-0 text-center text-xs font-medium text-gray-500 border-t border-b border-r border-gray-200 bg-[var(--content-bg-color)] ${isMobile ? '' : 'sticky left-0 z-20'}`}
        >
          <div
            className="h-full flex items-center justify-center bg-[var(--content-bg-color)]"
            style={{ height: '48px' }}
          >
            #
          </div>
        </th>
        {table.getHeaderGroups().map(headerGroup =>
          headerGroup.headers.map((header, headerIndex) => {
            const column = columns.find(c => c.id === header.id);

            // Make the first content column (headerIndex === 0) sticky in desktop scrolling mode only
            const isFirstContentColumn = headerIndex === 0;
            const shouldBeSticky = needsDesktopScrolling && isFirstContentColumn && !isMobile;

            return (
              <th
                key={header.id}
                className="p-0 text-left font-medium text-gray-700 border-t border-b border-r border-gray-200"
                style={{
                  height: '48px',
                  position: shouldBeSticky ? 'sticky' : undefined,
                  left: shouldBeSticky ? '40px' : undefined,
                  zIndex: shouldBeSticky ? 10 : undefined,
                  backgroundColor: shouldBeSticky ? 'white' : undefined,
                  borderRight: '1px solid #e5e7eb',
                }}
              >
                {header.isPlaceholder ? null : (
                  <div className="relative font-semibold h-full px-2">
                    {editingColumnId === column?.id ? (
                      <div className="absolute inset-0">
                        <input
                          type="text"
                          className="w-full h-full min-h-[40px] p-2 border border-blue-400 bg-white absolute inset-0 text-sm font-medium text-ellipsis"
                          defaultValue={column.name}
                          autoFocus
                          onBlur={e => {
                            // Save on blur
                            if (e.target.value !== column.name) {
                              const updatedColumn = { ...column, name: e.target.value };
                              updateColumn(tableDefinition?.id || '', column.id, updatedColumn);
                            }
                            setEditingColumnId(null);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              // Save on Enter
                              if (e.currentTarget.value !== column.name) {
                                const updatedColumn = {
                                  ...column,
                                  name: e.currentTarget.value,
                                };
                                updateColumn(tableDefinition?.id || '', column.id, updatedColumn);
                              }
                              setEditingColumnId(null);
                            } else if (e.key === 'Escape') {
                              // Discard changes on Escape
                              setEditingColumnId(null);
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="flex items-center justify-between group h-full w-full space-x-2">
                        <div
                          className="cursor-pointer flex-1 h-full flex justify-start items-center overflow-hidden"
                          onClick={() => setEditingColumnId(column?.id || null)}
                        >
                          <span className="truncate font-bold max-w-full block">
                            {column?.name}
                          </span>
                        </div>

                        <div className="flex items-center flex-shrink-0">
                          <div className="relative cursor-pointer text-gray-500 hover:text-blue-500 transition-colors">
                            <span
                              className="flex-shrink-0 opacity-100 group-hover:opacity-0 transition-opacity text-gray-400"
                              title={COLUMN_TYPES[column?.type || 'text']?.label}
                            >
                              {COLUMN_TYPES[column?.type || 'text']?.icon}
                            </span>

                            <button
                              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              onClick={() => {
                                // Create a ref for this column menu button if it doesn't exist
                                if (!columnMenuButtonRefs.current[column.id]) {
                                  columnMenuButtonRefs.current[column.id] = React.createRef();
                                }
                                setShowColumnTypeMenu(column?.id || null);
                              }}
                              ref={ref => {
                                if (ref && !columnMenuButtonRefs.current[column.id]) {
                                  columnMenuButtonRefs.current[column.id] = { current: ref };
                                }
                              }}
                              title="Column options"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                          </div>
                          {showColumnTypeMenu === column?.id && (
                            <ColumnTypeMenu
                              columnId={column.id}
                              currentType={column.type}
                              targetRef={columnMenuButtonRefs.current[column.id]}
                              onSelect={type => {
                                // Always allow changing from any type to text
                                if (type === 'text') {
                                  const updatedColumn = { ...column, type };
                                  updateColumn(tableDefinition?.id || '', column.id, updatedColumn);
                                  setShowColumnTypeMenu(null);
                                  return;
                                }

                                // Check if column has data
                                const rows = table.getCoreRowModel().rows;
                                const hasData = rows.some(row => {
                                  const cellValue = row.getValue(column.id);
                                  return (
                                    cellValue !== null &&
                                    cellValue !== undefined &&
                                    cellValue !== ''
                                  );
                                });

                                if (!hasData) {
                                  // If no data, allow any type change
                                  const updatedColumn = { ...column, type };
                                  updateColumn(tableDefinition?.id || '', column.id, updatedColumn);
                                  setShowColumnTypeMenu(null);
                                  return;
                                }

                                // Special case: allow text to number if all values are valid numbers
                                if (column.type === 'text' && type === 'number') {
                                  const allValuesAreNumbers = rows.every(row => {
                                    const cellValue = row.getValue(column.id);
                                    if (
                                      cellValue === null ||
                                      cellValue === undefined ||
                                      cellValue === ''
                                    ) {
                                      return true; // Empty values are fine
                                    }
                                    // Check if value is a valid number
                                    return (
                                      !isNaN(parseFloat(String(cellValue))) &&
                                      isFinite(Number(cellValue))
                                    );
                                  });

                                  if (allValuesAreNumbers) {
                                    const updatedColumn = { ...column, type };
                                    updateColumn(
                                      tableDefinition?.id || '',
                                      column.id,
                                      updatedColumn
                                    );
                                    setShowColumnTypeMenu(null);
                                    return;
                                  }
                                }

                                // Special case: handle conversion to select
                                if (type === 'select') {
                                  // Get unique values from the column to use as initial options
                                  const uniqueValues = new Set<string>();

                                  rows.forEach(row => {
                                    const cellValue = row.getValue(column.id);
                                    if (
                                      cellValue !== null &&
                                      cellValue !== undefined &&
                                      cellValue !== ''
                                    ) {
                                      // Handle boolean values specifically
                                      if (column.type === 'boolean') {
                                        // Convert boolean to string representation
                                        uniqueValues.add(cellValue ? 'True' : 'False');
                                      } else {
                                        uniqueValues.add(String(cellValue));
                                      }
                                    }
                                  });

                                  // Convert unique values to SelectOption objects
                                  const options = Array.from(uniqueValues).map(value => 
                                    createSelectOption(value)
                                  );

                                  // Update the column with new type and options
                                  const updatedColumn = {
                                    ...column,
                                    type,
                                    options: { options },
                                  };

                                  updateColumn(tableDefinition?.id || '', column.id, updatedColumn);
                                  setShowColumnTypeMenu(null);
                                  return;
                                }

                                // If we got here, the type change is not allowed
                                document.dispatchEvent(
                                  new CustomEvent('column-type-change-blocked', {
                                    detail: {
                                      columnName: column.name,
                                      fromType: column.type,
                                      toType: type,
                                    },
                                  })
                                );
                                setShowColumnTypeMenu(null);
                              }}
                              onClose={() => setShowColumnTypeMenu(null)}
                              columnIndex={columns.findIndex(c => c.id === column.id)}
                              columnsCount={columns.length}
                              onAddColumnAfter={onAddColumnAfter}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </th>
            );
          })
        )}
      </tr>
    </thead>
  );
}
